import { FC, useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import BluesiteTableComponent from 'views/components/UI/BluesiteTable'
import { MainLayout } from 'views/layouts'
import { userSelector } from 'ducks/user'
import { headers } from './constants'
import { H3 } from 'views/components/UI/Text'
import getInventoryByCompany from 'services/inventory/getByCompany'
import { DEFAULT_LIMIT } from 'constants/global.constants'
import { fetchEditInventoryStatus } from 'services/inventory/updateInventory'
import ModalLoader from 'views/components/UI/ModalLoader/modal-loader'
import { useNotification } from 'lib/context/notification.context'
import { BackButton, Button } from 'views/components/UI/Buttons'
import { renewInventory } from 'services/inventory/renewInventory'

type InventoryRow = {
  id: number
  name: string
  type: string
  credits: number | string
  tests_used: number
  tests_available: number | string
  assigned_credits?: number
  start_date: string
  expiration_date: string
  state: string
  subscriptionId?: number
  subscriptionName?: string
  companyId?: number
  suscription_company_id?: number
  usedCredits?: number
  total_page?: number
}

const InventoryScreen: FC = () => {
  const navigate = useNavigate()
  const role = useSelector((state: any) => state.auth.role)
  const { companyId } = useSelector(userSelector)
  const { getSuccess, getWarning } = useNotification()
  const [inventoryResult, setInventoryResult] = useState<InventoryRow[]>([])
  const [currentPage, setCurrentPage] = useState(1)
  const [totalPage, setTotalPage] = useState(0)
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [isUpdatingInventory, setIsUpdatingInventory] = useState(false)
  const [refreshInventory, setRefreshInventory] = useState(0)

  dayjs.extend(utc)

  const canRenewInventory =
    role === 'super_admin' || role === 'special_admin' || role === 'super_platform_admin'

  const handleRenewInventory = async (subscriptionCompanyId: number) => {
    const confirmed = window.confirm('Quieres renovar este inventario por 12 meses?')
    if (!confirmed) {
      return
    }

    try {
      setIsLoading(true)
      const response = await renewInventory(subscriptionCompanyId)

      if (response?.status === 'success') {
        getSuccess('Inventario renovado correctamente')
        setRefreshInventory((value: number) => value + 1)
        return
      }

      getWarning('No se pudo renovar el inventario')
    } catch (error) {
      getWarning('No se pudo renovar el inventario')
    } finally {
      setIsLoading(false)
    }
  }

  const InventoryActions: FC<InventoryRow> = ({ id }) => {
    return (
      <Button type="button" size="sm" variant="secondary" onClick={() => handleRenewInventory(id)}>
        Renovar
      </Button>
    )
  }

  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsLoading(true)
        const limit = DEFAULT_LIMIT
        const response = await getInventoryByCompany(companyId, currentPage, limit)

        if (!response || response.status === 'error') {
          setTotalPage(0)
          return
        }

        setTotalPage(response.total_pages)

        if (response.status === 'OK') {
          const res = response.data
          await Promise.all(
            res.map(async (item: any) => {
              const formatter = new Date()
              const today = dayjs(formatter).format('YYYY-MM-DDTHH:mm:ss.SSS[Z]')
              const expiration = item.expiration_date
              const updateData = {
                start_date: today,
                expiration_date: expiration,
              }

              if (item.usedCredits < item.credits && today < expiration && item.state !== true) {
                const updateDataActive = {
                  ...updateData,
                  credits: item.credits,
                  tests_used: item.usedCredits,
                }
                await fetchEditInventoryStatus(Number(item.id), updateDataActive)
                setIsUpdatingInventory(true)
              }

              if (today > expiration && item.state !== false) {
                await fetchEditInventoryStatus(Number(item.id), updateData)
              }

              return item
            }),
          )
          setIsLoading(false)
        }
      } catch (error) {
        setTotalPage(0)
      }
    }

    fetchData()
  }, [companyId, currentPage, refreshInventory])

  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsLoading(true)
        const limit = DEFAULT_LIMIT
        const response = await getInventoryByCompany(companyId, currentPage, limit)

        if (!response) {
          setTotalPage(0)
          return
        }

        setTotalPage(response.total_pages)

        if (response?.status === 'OK') {
          const res = response.data
          const formattedData = await Promise.all(
            res.map(async (item: any) => {
              const formattedItem: InventoryRow = {
                id: Number(item.id),
                name: item.subscriptionName,
                type: item.name,
                credits: parseInt(item.credits) > 900000000 ? '∞' : item.credits,
                tests_used: Math.min(parseInt(item.usedCredits), parseInt(item.credits)),
                tests_available:
                  parseInt(item.credits) > 900000000 ? '∞' : Math.max(0, parseInt(item.creditsAvailable)),
                assigned_credits: parseInt(item.credits),
                start_date: dayjs.utc(item.start_date).format('DD/MM/YYYY'),
                expiration_date: dayjs.utc(item.expiration_date).format('DD/MM/YYYY'),
                state: item.state ? 'Activo' : 'Inactivo',
                subscriptionId: item.subscriptionId,
                subscriptionName: item.subscriptionName,
                companyId: item.companyId,
                suscription_company_id: item.suscription_company_id,
                usedCredits: item.usedCredits,
                total_page: item.total_page,
              }

              return formattedItem
            }),
          )

          setInventoryResult(formattedData)
        }

        setIsLoading(false)
      } catch (error) {
        setTotalPage(0)
        return error
      }
    }

    if (!isUpdatingInventory) {
      fetchData()
    } else {
      setIsUpdatingInventory(false)
    }
  }, [companyId, currentPage, isUpdatingInventory, refreshInventory])

  return (
    <MainLayout>
      <>
        <div
          style={{
            display: 'flex',
            flexDirection: 'row',
            margin: '40px 0px 10px 0px',
            alignItems: 'center',
            gap: 16,
          }}
        >
          <BackButton onClick={() => navigate(-1)} />
          <H3>Inventarios</H3>
        </div>
        <div style={{ marginBottom: 30 }}>
          {!isUpdatingInventory && (
            <BluesiteTableComponent
              data={inventoryResult}
              headers={headers}
              ActionComponent={canRenewInventory ? InventoryActions : undefined}
              totalPages={totalPage}
              handlePagination={setCurrentPage}
              currentPage={currentPage}
              searchBar={false}
              searchLabel=""
              minimumColumnWidth={145}
              tableStyle="inventory"
            />
          )}
        </div>
      </>
      <ModalLoader isLoading={isLoading} />
    </MainLayout>
  )
}

export default InventoryScreen
